import { Box } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextFieldCompnent from "../../components/textFieldComponent";
import SelectComponent from "../../components/selectComponent";
import { getUserRoleLabel, UserRole } from "../../models/user.model";
import ButtonComponent from "../../components/ButtonComponent";
import { fetchAuthUser, selectAuthUser } from "../../features/authSlice";
import { useTranslation } from "react-i18next";

const UserForm = ({
  buttonText,
  handleFormSubmit,
  initialValues,
  loading,
  editingUserId = null,
  requirePassword = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const isSelfEdit =
    Boolean(user?._id && editingUserId) &&
    String(user._id) === String(editingUserId);

  const userSchema = yup.object().shape({
    email: yup.string().email("invalid email").required("Email est requis"),
    firstName: yup.string().required("Prénom est requis"),
    lastName: yup.string().required("Nom est requis"),
    role: yup.string().required("Role est requis"),
    password: requirePassword
      ? yup.string().required("Mot de passe est requis")
      : yup.string(),
    confirmPassword: requirePassword
      ? yup
          .string()
          .oneOf(
            [yup.ref("password"), null],
            "Les mots de passe doivent correspondre"
          )
          .required("Confirmation du mot de passe est requise")
      : yup
          .string()
          .oneOf(
            [yup.ref("password"), null],
            "Les mots de passe doivent correspondre"
          ),
  });

  useEffect(() => {
    if (!user) dispatch(fetchAuthUser());
  }, [dispatch, user]);

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={userSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit}>
          <Box
            display="grid"
            gap="16px"
            gridTemplateColumns="repeat(6, minmax(0, 1fr))"
            sx={{
              "& > div": { gridColumn: isNonMobile ? undefined : "span 6" },
            }}
          >
            <TextFieldCompnent
              type="email"
              label={t("auth.email")}
              change={handleChange}
              value={values.email}
              name="email"
              blur={handleBlur}
              touched={touched.email}
              error={errors.email}
              colum="span 3"
              row="1 / span 1"
              required
            />
            <TextFieldCompnent
              type="text"
              label={t("fields.firstName")}
              change={handleChange}
              value={values.firstName}
              name="firstName"
              blur={handleBlur}
              touched={touched.firstName}
              error={errors.firstName}
              colum="span 3"
              row="2 / span 1"
              required
            />
            <TextFieldCompnent
              type="text"
              label={t("fields.lastName")}
              change={handleChange}
              value={values.lastName}
              name="lastName"
              blur={handleBlur}
              touched={touched.lastName}
              error={errors.lastName}
              colum="span 3"
              row="3 / span 1"
              required
            />

            <SelectComponent
              gridColumn="span 3"
              gridRow="4 / span 1"
              name="role"
              label={t("table.role")}
              required
              disabled={isSelfEdit}
              items={Object.values(UserRole)
                .filter((role) => {
                  if (user?.role === UserRole.admin) {
                    return role !== UserRole.admin;
                  } else {
                    return role !== UserRole.admin && role !== UserRole.manager;
                  }
                })
                .map((role) => ({
                  _id: role,
                  name: getUserRoleLabel(role),
                }))}
              value={values.role}
              change={handleChange}
            />

            <TextFieldCompnent
              type="password"
              label={t("fields.password")}
              change={handleChange}
              value={values.password}
              name="password"
              blur={handleBlur}
              touched={touched.password}
              error={errors.password}
              colum="span 3"
              row="5 / span 1"
              required={requirePassword}
            />
            <TextFieldCompnent
              type="password"
              label={t("fields.confirmPassword")}
              change={handleChange}
              value={values.confirmPassword}
              name="confirmPassword"
              blur={handleBlur}
              touched={touched.confirmPassword}
              error={errors.confirmPassword}
              colum="span 3"
              row="6 / span 1"
              required={requirePassword}
            />
          </Box>
          <ButtonComponent text={buttonText} loading={loading} />
        </form>
      )}
    </Formik>
  );
};

export default UserForm;
